import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import { SearchableDropdown, FileUpload } from "../../components/ui";
import {
  financialRecordsApi,
  contactsApi,
  categoriesApi,
} from "../../services/api";
import type { FinancialRecordType } from "@akfdev-com/shared/src/models";

interface InvoiceItem {
  id: string;
  description: string;
  category?: string; // NEW: category ID
  categoryName?: string; // NEW: category name (read-only)
  quantity: number;
  unitPrice: number; // in pence
}

interface FormData {
  type: FinancialRecordType;
  buildingId: string;
  contactId?: string; // NEW: contact ID
  supplierName?: string;
  date: string;
  dueDate?: string;
  description?: string;
  title?: string;
  invoiceNumber?: string;
  supplierAddress?: string;
  reference?: string;
  category?: string;
  nominalCode?: string;
  amount?: number; // in pence, computed from items if present
  items: InvoiceItem[];
  receiptUrl?: string;
  documentUrl?: string;
}

const FinancialRecordCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBuilding } = useBuilding();
  const presetType =
    (searchParams.get("type") as FinancialRecordType) || "invoice";
  const presetBuilding =
    searchParams.get("building") || currentBuilding?.id || "";

  const [formData, setFormData] = useState<FormData>({
    type: presetType,
    buildingId: presetBuilding,
    date: new Date().toISOString().split("T")[0],
    items: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState<string | null>(null);

  const isInvoice = formData.type === "invoice";
  const isBill = formData.type === "bill";
  const isPayment = formData.type === "payment";

  // Compute amount from items if present
  useEffect(() => {
    if (formData.items.length > 0) {
      const total = formData.items.reduce((sum, item) => {
        return sum + item.quantity * item.unitPrice;
      }, 0);
      setFormData((prev) => ({ ...prev, amount: total }));
    }
  }, [formData.items]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddItem = () => {
    const newItem: InvoiceItem = {
      id: `item-${Date.now()}`,
      description: "",
      quantity: 1,
      unitPrice: 0,
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleUpdateItem = (index: number, field: string, value: any) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.buildingId) {
      newErrors.buildingId = "Building is required";
    }

    if (!formData.date) {
      newErrors.date = "Date is required";
    }

    if (isInvoice) {
      if (!formData.contactId) {
        newErrors.contactId = "Supplier/Contact is required";
      }
      if (formData.items.length === 0) {
        newErrors.items = "At least one line item is required for invoices";
      }
      formData.items.forEach((item, idx) => {
        if (!item.description) {
          newErrors[`item_${idx}_description`] = "Description required";
        }
        if (item.quantity <= 0) {
          newErrors[`item_${idx}_quantity`] = "Quantity must be positive";
        }
        if (item.unitPrice < 0) {
          newErrors[`item_${idx}_unitPrice`] =
            "Unit price must be non-negative";
        }
      });
    }

    if (isBill || isPayment) {
      if (!formData.contactId) {
        newErrors.contactId = "Supplier is required";
      }
      if (!formData.amount || formData.amount <= 0) {
        newErrors.amount = "Amount must be greater than 0";
      }
      if (!Number.isInteger(formData.amount)) {
        newErrors.amount = "Amount must be a whole number (pence)";
      }
    }

    if (formData.dueDate && formData.date && formData.dueDate < formData.date) {
      newErrors.dueDate = "Due date must be after the record date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      // Create the record
      const payload = {
        type: formData.type,
        buildingId: formData.buildingId,
        contactId: formData.contactId,
        supplierName: formData.supplierName,
        date: formData.date,
        dueDate: formData.dueDate,
        description: formData.description,
        title: formData.title,
        invoiceNumber: formData.invoiceNumber,
        supplierAddress: formData.supplierAddress,
        reference: formData.reference,
        category: formData.category,
        nominalCode: formData.nominalCode,
        amount: formData.amount,
        items: formData.items.map(({ id, ...item }) => item), // Remove client-side IDs
        receiptUrl: formData.receiptUrl,
        documentUrl: formData.documentUrl,
      };

      const response = await financialRecordsApi.create(payload);
      const newRecord = response.record;
      setRecordId(newRecord.id);

      // Redirect to detail page on success
      navigate(
        `/dashboard/financial-records/${newRecord.id}?buildingId=${formData.buildingId}`,
      );
    } catch (error: any) {
      setSaveError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save record",
      );
      setSaving(false);
    }
  };

  const handleContactSelect = (contact: any) => {
    handleFieldChange("contactId", contact.id);
    handleFieldChange("supplierName", contact.name);
  };

  const handleCategorySelect = (itemIndex: number, category: any) => {
    handleUpdateItem(itemIndex, "category", category.id);
    handleUpdateItem(itemIndex, "categoryName", category.name);
  };

  const computeLineTotal = (item: InvoiceItem) => {
    return item.quantity * item.unitPrice;
  };

  const computeInvoiceTotal = () => {
    return formData.items.reduce(
      (sum, item) => sum + computeLineTotal(item),
      0,
    );
  };

  const formatPence = (pence: number) => {
    return ((pence || 0) / 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Create ${
          formData.type.charAt(0).toUpperCase() + formData.type.slice(1)
        }`}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {saveError && <ErrorAlert message={saveError} />}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
          <div className="p-6 space-y-6">
            {/* Type & Date Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Type <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    !recordId && handleFieldChange("type", e.target.value)
                  }
                  disabled={!!recordId}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    recordId
                      ? "bg-gray-100 cursor-not-allowed text-gray-500"
                      : "bg-white"
                  } ${errors.type ? "border-red-600" : "border-gray-300"}`}
                >
                  <option value="invoice">Invoice</option>
                  <option value="bill">Bill</option>
                  <option value="payment">Payment</option>
                </select>
                {errors.type && (
                  <p className="text-red-600 text-xs mt-1">{errors.type}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleFieldChange("date", e.target.value)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.date ? "border-red-600" : "border-gray-300"
                  }`}
                />
                {errors.date && (
                  <p className="text-red-600 text-xs mt-1">{errors.date}</p>
                )}
              </div>

              {(isBill || isPayment) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate || ""}
                    onChange={(e) =>
                      handleFieldChange("dueDate", e.target.value || undefined)
                    }
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.dueDate ? "border-red-600" : "border-gray-300"
                    }`}
                  />
                  {errors.dueDate && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.dueDate}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Contact/Supplier Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {isInvoice ? "Supplier/Contact" : "Contact"}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <SearchableDropdown
                  placeholder="Search contacts..."
                  onSearch={(term) =>
                    contactsApi.getAll({
                      search: term,
                      buildingId: formData.buildingId,
                    })
                  }
                  onSelect={handleContactSelect}
                  formatLabel={(item) => `${item.name} (${item.type})`}
                  createNewLabel="+ Create new contact"
                  onCreateNew={() =>
                    navigate(
                      `/dashboard/contacts/new?return-to=financial-records/new&type=${formData.type}`,
                    )
                  }
                />
                {errors.contactId && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.contactId}
                  </p>
                )}
                {formData.supplierName && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: <strong>{formData.supplierName}</strong>
                  </p>
                )}
              </div>

              {formData.type === "invoice" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    value={formData.invoiceNumber || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "invoiceNumber",
                        e.target.value || undefined,
                      )
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Description */}
            {formData.type !== "invoice" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "description",
                      e.target.value || undefined,
                    )
                  }
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Line Items for Invoices */}
            {isInvoice && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Line Items <span className="text-red-600">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    disabled={saving}
                  >
                    + Add Item
                  </button>
                </div>

                {errors.items && (
                  <p className="text-red-600 text-xs mb-2">{errors.items}</p>
                )}

                {formData.items.length > 0 ? (
                  <div className="overflow-x-auto relative">
                    <table className="min-w-full border-collapse border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 px-2 py-2 text-left text-xs font-medium text-gray-700">
                            Description
                          </th>
                          <th className="border border-gray-300 px-2 py-2 text-left text-xs font-medium text-gray-700">
                            Category
                          </th>
                          <th className="border border-gray-300 px-2 py-2 text-right text-xs font-medium text-gray-700">
                            Qty
                          </th>
                          <th className="border border-gray-300 px-2 py-2 text-right text-xs font-medium text-gray-700">
                            Unit Price (£)
                          </th>
                          <th className="border border-gray-300 px-2 py-2 text-right text-xs font-medium text-gray-700">
                            Total (£)
                          </th>
                          <th className="border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-700">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, idx) => (
                          <tr key={item.id}>
                            <td className="border border-gray-300 px-2 py-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    idx,
                                    "description",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g., Maintenance work"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                              {errors[`item_${idx}_description`] && (
                                <p className="text-red-600 text-xs mt-1">
                                  {errors[`item_${idx}_description`]}
                                </p>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-2 relative z-30">
                              <SearchableDropdown
                                placeholder="Category..."
                                onSearch={(term) =>
                                  categoriesApi.getAll({
                                    search: term,
                                    type: "line_item",
                                  })
                                }
                                onSelect={(cat) =>
                                  handleCategorySelect(idx, cat)
                                }
                                formatLabel={(cat) => cat.name}
                                createNewLabel="+ New category"
                                onCreateNew={() =>
                                  navigate(
                                    `/dashboard/categories/new?type=line_item&return-to=financial-records/new`,
                                  )
                                }
                              />
                              {item.categoryName && (
                                <p className="text-xs text-gray-600 mt-1">
                                  {item.categoryName}
                                </p>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    idx,
                                    "quantity",
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                              {errors[`item_${idx}_quantity`] && (
                                <p className="text-red-600 text-xs mt-1">
                                  {errors[`item_${idx}_quantity`]}
                                </p>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={(item.unitPrice / 100).toFixed(2)}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    idx,
                                    "unitPrice",
                                    Math.round(
                                      parseFloat(e.target.value) * 100,
                                    ) || 0,
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                              {errors[`item_${idx}_unitPrice`] && (
                                <p className="text-red-600 text-xs mt-1">
                                  {errors[`item_${idx}_unitPrice`]}
                                </p>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-right text-sm font-medium">
                              £{formatPence(computeLineTotal(item))}
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(idx)}
                                className="text-red-600 hover:text-red-800 text-sm"
                                aria-label={`Remove item ${idx + 1}`}
                                disabled={saving}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No items yet. Click "+ Add Item" to start.
                  </p>
                )}

                {formData.items.length > 0 && (
                  <div className="mt-4 text-right">
                    <p className="text-lg font-semibold">
                      Total: £{formatPence(computeInvoiceTotal())}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Amount for Bills/Payments */}
            {(isBill || isPayment) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Amount (£) <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      formData.amount !== undefined
                        ? (formData.amount / 100).toFixed(2)
                        : ""
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        "amount",
                        e.target.value
                          ? Math.round(parseFloat(e.target.value) * 100)
                          : undefined,
                      )
                    }
                    placeholder="0.00"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.amount ? "border-red-600" : "border-gray-300"
                    }`}
                  />
                  {errors.amount && (
                    <p className="text-red-600 text-xs mt-1">{errors.amount}</p>
                  )}
                </div>
              </div>
            )}

            {/* Document Upload Section (Post-Create) */}
            {recordId && (
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-4">
                  Documents (Optional)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FileUpload
                      label="Receipt"
                      onFileSelect={async (file, presignedUrl, s3Url) => {
                        handleFieldChange("receiptUrl", s3Url);
                      }}
                      onRemove={() =>
                        handleFieldChange("receiptUrl", undefined)
                      }
                      currentFile={formData.receiptUrl}
                    />
                  </div>
                  <div>
                    <FileUpload
                      label="Supporting Document"
                      onFileSelect={async (file, presignedUrl, s3Url) => {
                        handleFieldChange("documentUrl", s3Url);
                      }}
                      onRemove={() =>
                        handleFieldChange("documentUrl", undefined)
                      }
                      currentFile={formData.documentUrl}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg flex items-center justify-between">
            <Link
              to="/dashboard/financial-records"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-t-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Record"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancialRecordCreateForm;
